import dayjs from "dayjs";
import {jsonArrayMember, jsonMember, jsonObject, TypedJSON} from "typedjson";
import { v4 as uuidv4 } from 'uuid';
@jsonObject()
export class StateChange {
    @jsonMember(String)
    new: string;

    @jsonMember(String)
    old: string;
}

const numericOptions = {
    deserializer: (value: unknown) => {
        return (typeof value === 'string') ? parseFloat(value) : Number(value)
    }
};

@jsonObject()
export class CellReport {
    @jsonMember(Number, numericOptions)
    lac: number;

    @jsonMember(Number, numericOptions)
    cellId: number;

    @jsonMember(Number, numericOptions)
    rxLevel: number;
}

/**
 * Base class for sample reports, containing common fields like temperature, humidity.
 * These fields are sometimes sent in multiple clubbed reports as well.
 */
@jsonObject()
export class BaseSampleReport {

    @jsonMember(String, {
        deserializer: (value: any) => {
            return value ? value.toLowerCase() : uuidv4();
        }
    })
    uuid: string;

    @jsonMember(() => dayjs.Dayjs, {
        deserializer: (value: any) => {
            return value && dayjs(value);
        }
    })
    creationDate: dayjs.Dayjs;

    @jsonMember(Number, numericOptions)
    temperature: number;

    @jsonMember(Number, numericOptions)
    humidity: number;

    @jsonMember(Number, numericOptions)
    light: number;

    @jsonMember(Boolean)
    is_light_detected: boolean;

    @jsonMember(String)
    moving: string;

    @jsonMember(String)
    acc_x: string;

    @jsonMember(String)
    acc_y: string;

    @jsonMember(String)
    acc_z: string;
}

@jsonObject()
export class EventUpdateData extends BaseSampleReport {
    @jsonMember(Number, numericOptions)
    eventCode: number;

    @jsonMember(String)
    protocol: string;

    @jsonMember(String)
    device_model: string;

    @jsonMember(String)
    imei: string;

    @jsonMember(String)
    reportId: string;

    @jsonMember(String)
    reportType: string;

    @jsonMember(String)
    radio: string;

    @jsonMember(Number, numericOptions)
    mcc: number;

    @jsonMember(Number, numericOptions)
    mnc: number;

    @jsonMember(Number, numericOptions)
    lac: number;

    @jsonMember(Number, numericOptions)
    cellID: number;

    @jsonMember(Number, numericOptions)
    neighbourCellCount: number;

    @jsonMember(String)
    lac_hex: string;

    @jsonMember(String)
    cellID_hex: string;

    @jsonMember(String, {
        deserializer: (value: unknown) => {
            switch (typeof value) {
                case 'object':
                    return JSON.stringify(value, null, 2);

                default:
                    return value;
            }
        }
    })
    address: string;

    @jsonMember(String)
    location: string;

    @jsonArrayMember(() => BaseSampleReport)
    sampledReports: BaseSampleReport[];

    @jsonArrayMember(CellReport)
    cellReports: CellReport[];

    @jsonMember(String)
    accessoryVoltage: string;

    @jsonMember(Number, numericOptions)
    battery_level: number;

    @jsonMember(Boolean)
    in_flight_mode: boolean;

    @jsonMember(Boolean)
    ignition: boolean;

    @jsonMember(() => dayjs.Dayjs, {
        deserializer: (value: any) => {
            return value ? dayjs(`${value}.000Z`) : null;
        }
    })
    reported_at: dayjs.Dayjs;


    get report_time(): dayjs.Dayjs | null {
        return this.reported_at ? this.reported_at : null;
    }
}


@jsonObject()
export class EventUpdate {
    @jsonMember(String)
    sourceId: string;

    @jsonMember(String)
    sourceName: string;

    @jsonMember(String)
    date: string;

    @jsonMember(String)
    message: string;

    @jsonMember(String)
    comment: string;

    @jsonMember(String)
    user: string;

    @jsonMember(String)
    state: string;

    @jsonMember(() => dayjs.Dayjs, {
        deserializer: (value: any) => {
            return value && dayjs(value);
        }
    })
    creationDate: dayjs.Dayjs;

    @jsonMember(String, {
        name: 'data',
        deserializer: (value: unknown) => {
            if (typeof value === 'object' && value !== null) {
                try {
                    return JSON.stringify(value);
                } catch (e) {
                    return value; // Fallback to raw data if parsing fails
                }
            }
            return value;
        }
    })
    _rawData: string;

    get data(): EventUpdateData {
        if (this._rawData) {
            try {
                return EventUpdateDataSerializer.parse(this._rawData);
            } catch (e) {
                console.error('Failed to parse event update data:', e);
                return new EventUpdateData(); // Return an empty instance if parsing fails
            }
        }
        return new EventUpdateData(); // Return an empty instance if no data is available
    }

    get rawData(): Object {
        try {
            return (typeof this._rawData === 'string') ? JSON.parse(this._rawData) : this._rawData;
        } catch (e) {
            return {
                message: 'Invalid JSON data received in App',
                error: e.message,
                rawData: this._rawData
            }
        }
    }

    set rawData(value: unknown) {
        this._rawData = (typeof value === 'object') ? JSON.stringify(value) : value.toString();
    }
}


export const EventUpdateSerializer = new TypedJSON(EventUpdate);
export const EventUpdateDataSerializer = new TypedJSON(EventUpdateData);
